import moment from 'moment';

const IS_DEV_MODE = process.env.NODE_ENV !== 'production';

const XlConnectorFeature = {
  Admin: 'admin',
  Team: 'team',
  Personal: 'personal'
};

export const Plan = {
  Trial: 'Trial',
  Admin: 'Enterprise Admin',
  Team: 'Team Player',
  Personal: 'Personal Edition',
  Free: 'Free',
};

export const SubscriptionStatus = {
  Active: 'active',
  Trial: 'in_trial',
  Cancelled: 'cancelled',
  Paused: 'paused',
  NonRenewing: 'non_renewing',
};

export const PortalAction = {
  Subscribe: 'subscribe',
  Manage: 'manage',
};


export const ALL_PLANS = Object.values(Plan);
export const ADMIN_LIKE_PLANS = [Plan.Trial, Plan.Admin];

export class Feature {
  constructor(license, plans, { description = '', isDisabled = false } = {}) {
    this.license = license;
    this.plans = plans;
    this.isEnabled = !isDisabled;
    this.description = description;
  }

  get can() { return this.isEnabled && this.license._checkPlan(this.plans); }
  get isDisabled() { return !this.isEnabled; }
  set isDisabled(value) { this.isEnabled = !value; }
}

// Three plans (features):
// Trial - Enterprise Admin mode for 30 days. Trial message with a button to buy shows on every page that is not free: “30-day free trial. Days remaining: n <Purchase a license>
// Scheduler needs to stop working when trial expires.
// Free – Only Reports, SOQL, and Refresh functions available. Refresh All not available.
// Enterprise Admin - has all functionality
// Personal Edition - has access to Pull and Push. No access to Scheduler. Flow steps not visible. When doing Update/Insert/Upsert – no Save or Save As button, only Execute.
// Team Player - has access to Pull, in Push can only view and run flow steps. Cannot create/edit/delete flow steps. Scheduler not visible.

export function excludePlans(plans) {
  return ALL_PLANS.filter(x => !plans.includes(x));
}

export class License {
  constructor(license = {}) {
    const { status, valid_through = '', features = [] } = license;

    this.lastCheck = moment();
    this.expiresAt = moment(valid_through);
    this._remainingTimeMs = this.expiresAt.diff(this.lastCheck);
    this.isActive = SubscriptionStatus.Paused !== status && SubscriptionStatus.Cancelled !== status && this._remainingTimeMs > 0;
    this.feature = features.length ? features[0] : '';

    const getPlan = () => {
      if (!this.isActive) { return Plan.Free; }
      if (SubscriptionStatus.Trial === status) { return Plan.Trial; }

      const plans = {
        [XlConnectorFeature.Admin]: Plan.Admin,
        [XlConnectorFeature.Team]: Plan.Team,
        [XlConnectorFeature.Personal]: Plan.Personal,
      };

      return plans[this.feature] ?? Plan.Free;
    }

    this.plan = getPlan();
    this._license = license;

    this.scheduler = {
      use: new Feature(this, ADMIN_LIKE_PLANS),
    };
  }

  // Raw data from license returned from ChargeBee
  get rawLicense() { return this._license; }
  get rawFeatures() { return this._license.features; }
  get rawSubscriptionStatus() { return this._license.status; }
  get rawPlanName() { return this._license.plan_name; }
  get rawValidThrough() { return this._license.valid_through; }

  // Calculated plans based on features and subscription status
  get hasFreePlan() { return Plan.Free === this.plan; }
  get hasTrialPlan() { return Plan.Trial === this.plan; }
  get hasAdminPlan() { return Plan.Admin === this.plan; }
  get hasPersonalPlan() { return Plan.Personal === this.plan; }
  get hasTeamPlan() { return Plan.Team === this.plan; }

  _checkPlan(plans) {
    return plans.includes(this.plan);
  }

  makeFeature(plans, params) { return new Feature(this, plans, params); }
}

/* Temporary solution to allow access to all features while a demo period for Hubspot is going */
export const allowEverything = (rawLicense) => {
  return {
    ...rawLicense,
    features: ['admin'],
    options: rawLicense.options?.map(option => ({...option, value: true}))
  }
}
